import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAppState } from '../src/contexts/AppStateContext';
import api from '../src/utils/axiosConfig';

const EventAccessManagement = () => {
    const { currentEvent, currentOrganization } = useAppState();
    const [loading, setLoading] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [eventUsers, setEventUsers] = useState([]);
    const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Fetch data when currentEvent or currentOrganization changes
    useEffect(() => {
        const fetchData = async () => {
            if (!currentEvent || !currentEvent.id || !currentOrganization) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Fetch all users and their access state from the access management endpoint
                const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/access-management/`);

                // All users are returned from this endpoint
                setAllUsers(response.data);

                // Filter to get only users with access (both admin and non-admin users)
                const usersWithAccess = response.data.filter(user => user.role === 'admin' || user.has_event_access);
                setEventUsers(usersWithAccess);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError("Failed to load access management data. Please try again.");
                setLoading(false);
            }
        };

        fetchData();
    }, [currentEvent, currentOrganization]);

    // Get users that don't already have event access
    const availableUsers = allUsers.filter(user =>
        !user.has_event_access && user.role !== 'admin'
    );

    // Format users for react-select
    const formatUsersForSelect = (users) => {
        return users.map(user => ({
            value: user.user.id, // Use user.id instead of membership.id
            label: `${user.user.first_name} ${user.user.last_name} (${user.user.email})`,
            user: user
        }));
    };

    // Add selected users to event access
    const addUsersToEvent = async () => {
        if (!currentEvent || selectedUsersToAdd.length === 0) return;

        setIsUpdating(true);
        setError(null);
        setSuccess(null);

        try {
            const userIdsToAdd = selectedUsersToAdd.map(option => option.value); // Already using user.id

            // Call API to add users to event access
            await api.post(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/access-management/`, {
                user_ids: userIdsToAdd
            });

            // Refresh the event users list
            const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/access-management/`);

            // Update both lists
            setAllUsers(response.data);

            // Filter to get only users with access
            const usersWithAccess = response.data.filter(user => user.role === 'admin' || user.has_event_access);
            setEventUsers(usersWithAccess);

            setSuccess("Users successfully added to event");
            setSelectedUsersToAdd([]);
        } catch (error) {
            console.error("Error adding users to event:", error);
            setError("Failed to add users to event. Please try again.");
        } finally {
            setIsUpdating(false);

            // Clear success message after 3 seconds
            if (success) {
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            }
        }
    };

    // Remove user from event access
    const removeUserFromEvent = async (userId) => {
        if (!currentEvent) return;

        setIsUpdating(true);
        setError(null);
        setSuccess(null);

        try {
            const userToRemove = allUsers.find(user => user.id === userId);
            if (!userToRemove) {
                setError("User not found");
                return;
            }

            await api.delete(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/access-management/`, {
                data: {
                    user_ids: [userToRemove.user.id] 
                }
            });

            // Refresh the complete list to ensure we have the latest data
            const response = await api.get(`/organizations/${currentOrganization.id}/events/${currentEvent.id}/access-management/`);

            // Update both lists
            setAllUsers(response.data);

            // Filter to get only users with access
            const usersWithAccess = response.data.filter(user => user.has_event_access || user.role === 'admin');
            setEventUsers(usersWithAccess);

            setSuccess("User successfully removed from event");
        } catch (error) {
            console.error("Error removing user from event:", error);
            setError("Failed to remove user from event. Please try again.");
        } finally {
            setIsUpdating(false);

            // Clear success message after 3 seconds
            if (success) {
                setTimeout(() => {
                    setSuccess(null);
                }, 3000);
            }
        }
    };

    // Custom styles for react-select
    const selectStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#cbd5e0',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#4f46e5'
                : state.isFocused
                    ? '#eef2ff'
                    : null,
            color: state.isSelected ? 'white' : '#2d3748',
            padding: '8px 12px',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e0e7ff',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#4f46e5',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#4f46e5',
            '&:hover': {
                backgroundColor: '#c7d2fe',
                color: '#4f46e5',
            },
        }),
    };

    if (!currentEvent) {
        return <div className="p-6 text-center">Please select an event to manage access</div>;
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center p-8">
                <svg className="animate-spin h-8 w-8 text-indigo-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-indigo-600">Loading access data...</span>
            </div>
        );
    }

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-6">Manage Event Access</h3>

            {/* Notifications */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
                    <AlertCircle size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700">{error}</p>
                </div>
            )}

            {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
                    <CheckCircle size={20} className="text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-green-700">{success}</p>
                </div>
            )}

            {/* Add Users Section */}
            <div className="space-y-4">
                <h4 className="font-medium text-gray-700">Add Users to Event</h4>
                <div className="space-y-2">
                    <label htmlFor="add-users" className="text-sm text-gray-600 block">
                        Select users to add:
                    </label>
                    <Select
                        id="add-users"
                        isMulti
                        options={formatUsersForSelect(availableUsers)}
                        value={selectedUsersToAdd}
                        onChange={setSelectedUsersToAdd}
                        placeholder="Search users by name or email..."
                        noOptionsMessage={() => "No more users available"}
                        styles={selectStyles}
                        className="mt-1"
                        isDisabled={isUpdating}
                    />
                </div>
                <button
                    onClick={addUsersToEvent}
                    disabled={selectedUsersToAdd.length === 0 || isUpdating}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors w-full mt-2"
                >
                    {isUpdating ? (
                        <span className="flex items-center justify-center">
                            <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing...
                        </span>
                    ) : (
                        'Add Selected Users'
                    )}
                </button>
            </div>

            {/* Current Access List */}
            <div className="mt-8">
                <h4 className="font-medium text-gray-700 mb-3">Current Access List</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
                    <div className="p-3 border-b border-gray-200 bg-gray-100">
                        <div className="grid grid-cols-4 gap-4">
                            <div className="font-medium text-gray-700">Name</div>
                            <div className="font-medium text-gray-700">Email</div>
                            <div className="font-medium text-gray-700">Role</div>
                            <div className="font-medium text-gray-700">Actions</div>
                        </div>
                    </div>
                    <div className="overflow-y-auto max-h-64">
                        {eventUsers.length === 0 ? (
                            <div className="p-4 text-gray-500 text-center">No users have access to this event</div>
                        ) : (
                            <div>
                                {eventUsers.map(userAccess => (
                                    <div
                                        key={userAccess.id}
                                        className={`p-3 border-b border-gray-200 last:border-b-0 ${userAccess.role === "admin" ? 'bg-gray-50' : ''}`}
                                    >
                                        <div className="grid grid-cols-4 gap-4">
                                            <div className="flex items-center">
                                                <span>{`${userAccess.user.first_name} ${userAccess.user.last_name}`}</span>
                                                {userAccess.role === "admin" && (
                                                    <span className="ml-2 text-xs bg-gray-200 text-gray-800 px-2 py-0.5 rounded-full">Admin</span>
                                                )}
                                            </div>
                                            <div className="text-gray-600">{userAccess.user.email}</div>
                                            <div className="text-gray-600 capitalize">
                                                {userAccess.role.replace('_', ' ')}
                                            </div>
                                            <div>
                                                {userAccess.role !== "admin" ? (
                                                    <button
                                                        onClick={() => removeUserFromEvent(userAccess.id)}
                                                        disabled={isUpdating}
                                                        className="text-red-500 hover:bg-red-100 p-1 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                ) : (
                                                    <span className="text-xs text-gray-500">
                                                        Admin users always have access
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Admin users always have access to all events and cannot be removed.
                </p>
            </div>
        </div>
    );
};

export default EventAccessManagement;